import { useEffect, useRef, type MouseEvent } from 'react'
import { Link } from '@tanstack/react-router'
import { Download } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { ProductSummaryList, type ProductSummaryItem } from '@/components/product-summary-list'
import { POShipmentLine } from '@/components/po-shipment-line'
import { OrderStatusChips } from '@/components/order-status-chips'
import { isActiveShipment } from '@/lib/shipments'
import { cn, formatMoney, parseUTCDate } from '@/lib/utils'
import { formatPoStatus, getPoStatusStyle } from '@/lib/status'
import type { components } from '@/api/schema'

type PurchaseOrder = components['schemas']['PurchaseOrderListRead']
type PurchaseOrderLine = components['schemas']['PurchaseOrderListLineRead']

const PRODUCT_LINE_KINDS = new Set(['main', 'custom'])
const MAX_SHIPMENT_LINES = 2

function productItems(po: PurchaseOrder): ProductSummaryItem[] {
  return (po.line_items ?? [])
    .filter((line) => line.parent_line_item_id == null && PRODUCT_LINE_KINDS.has(line.line_kind ?? 'main'))
    .map((line: PurchaseOrderLine) => ({
      id: line.id,
      title: line.description ? `${line.model} — ${line.description}` : line.model,
      quantity: line.quantity,
      productUuid: line.product?.uuid,
      imageUrl: line.product?.image_url,
    }))
}

function shipToLocation(po: PurchaseOrder) {
  return [po.ship_to_city, po.ship_to_state].filter(Boolean).join(', ')
}

/** The project name, unless it only repeats the order number ("Shopify Order #1364"). */
function distinctProjectName(po: PurchaseOrder) {
  const name = po.internal_order?.project?.name
  const orderNumber = po.internal_order?.order_number
  if (!name) return null
  if (orderNumber && name.includes(orderNumber)) return null
  return name
}

function PoStatusPill({ status, className = 'text-xs' }: { status: string; className?: string }) {
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 font-medium ${className}`}
      style={getPoStatusStyle(status)}
    >
      {formatPoStatus(status)}
    </span>
  )
}

/** Order / Shipping / AP from the PO's items (USA-1202); the stored PO status until it loads. */
function PoStatuses({ po, className }: { po: PurchaseOrder; className?: string }) {
  const summary = po.status_summary
  if (!summary) return <PoStatusPill status={po.status} className={className} />
  return <OrderStatusChips compact order={summary.order} shipping={summary.shipping} ap={summary.ap} />
}

function SelectBox({
  label,
  checked,
  indeterminate = false,
  onChange,
}: {
  label: string
  checked: boolean
  indeterminate?: boolean
  onChange: (checked: boolean) => void
}) {
  const ref = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate
  }, [indeterminate])
  return (
    <input
      ref={ref}
      type="checkbox"
      className="size-4 cursor-pointer accent-primary"
      aria-label={label}
      checked={checked}
      onChange={(event) => onChange(event.target.checked)}
    />
  )
}

function OpenActionsBadge({ count }: { count: number }) {
  if (count <= 0) return null
  return (
    <span
      className="inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-red-500 px-1 text-[11px] font-bold text-white"
      title={`${count} open action${count > 1 ? 's' : ''}`}
    >
      {count}
    </span>
  )
}

function ShippingSummary({ po, linkReference }: { po: PurchaseOrder; linkReference: boolean }) {
  const shipments = (po.shipments ?? []).filter(isActiveShipment)
  if (!shipments.length) {
    // The Shipping status already says so.
    if (po.status_summary?.shipping) return null
    return <span className="text-xs text-muted-foreground">Not shipped</span>
  }
  const shown = shipments.slice(0, MAX_SHIPMENT_LINES)
  return (
    <div className="space-y-1">
      {shown.map((shipment) => (
        <POShipmentLine key={shipment.id} shipment={shipment} linkReference={linkReference} />
      ))}
      {shipments.length > shown.length && (
        <p className="text-[11px] text-muted-foreground">
          +{shipments.length - shown.length} more
        </p>
      )}
    </div>
  )
}

export function PurchaseOrdersTableSkeleton({ rows = 8 }: { rows?: number }) {
  return (
    <div role="status" aria-label="Loading purchase orders">
      <div className="hidden lg:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Purchase Order</TableHead>
              <TableHead>Order</TableHead>
              <TableHead className="hidden xl:table-cell">Products</TableHead>
              <TableHead>Shipping</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: rows }).map((_, i) => (
              <TableRow key={i}>
                <TableCell>
                  <div className="space-y-1.5">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-20" />
                    <Skeleton className="h-5 w-24" />
                  </div>
                </TableCell>
                <TableCell>
                  <div className="space-y-1.5">
                    <Skeleton className="h-4 w-16" />
                    <Skeleton className="h-3 w-28" />
                  </div>
                </TableCell>
                <TableCell className="hidden xl:table-cell">
                  <div className="space-y-1">
                    <Skeleton className="h-7 w-full" />
                    <Skeleton className="h-7 w-3/4" />
                  </div>
                </TableCell>
                <TableCell>
                  <Skeleton className="h-5 w-40" />
                </TableCell>
                <TableCell>
                  <div className="flex flex-col items-end gap-1">
                    <Skeleton className="h-4 w-20" />
                    <Skeleton className="h-3 w-16" />
                  </div>
                </TableCell>
                <TableCell />
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="space-y-2 lg:hidden">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="space-y-2 rounded-lg border bg-card p-3">
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-3 w-1/3" />
            <Skeleton className="h-7 w-full" />
          </div>
        ))}
      </div>
    </div>
  )
}

export function PurchaseOrdersTable({
  items,
  onDownloadPdf,
  selectedIds,
  onSelectedIdsChange,
}: {
  items: PurchaseOrder[]
  onDownloadPdf: (uuid: string, filename?: string) => void
  /** Ticked POs; pass with `onSelectedIdsChange` to show tick boxes. */
  selectedIds?: ReadonlySet<number>
  onSelectedIdsChange?: (ids: Set<number>) => void
}) {
  const selectable = !!onSelectedIdsChange
  const isSelected = (po: PurchaseOrder) => !!selectedIds?.has(po.id)
  const selectedOnPage = items.filter(isSelected).length
  const setSelected = (po: PurchaseOrder, checked: boolean) => {
    const next = new Set(selectedIds)
    if (checked) next.add(po.id)
    else next.delete(po.id)
    onSelectedIdsChange?.(next)
  }
  const setAll = (checked: boolean) => {
    const next = new Set(selectedIds)
    for (const po of items) {
      if (checked) next.add(po.id)
      else next.delete(po.id)
    }
    onSelectedIdsChange?.(next)
  }

  return (
    <>
      {/* Desktop table */}
      <div className="hidden lg:block">
        <Table>
          <TableHeader>
            <TableRow>
              {selectable && (
                <TableHead className="w-10">
                  <SelectBox
                    label="Select all purchase orders on this page"
                    checked={items.length > 0 && selectedOnPage === items.length}
                    indeterminate={selectedOnPage > 0 && selectedOnPage < items.length}
                    onChange={setAll}
                  />
                </TableHead>
              )}
              <TableHead>Purchase Order</TableHead>
              <TableHead>Order</TableHead>
              <TableHead className="hidden xl:table-cell">Products</TableHead>
              <TableHead>Shipping</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((po) => {
              const order = po.internal_order
              const project = order?.project
              const projectName = distinctProjectName(po)
              const location = shipToLocation(po)
              const customerName = order?.customer_group?.name ?? po.ship_to_name
              const quotedFreight = po.quoted_freight_cost != null ? Number(po.quoted_freight_cost) : null

              return (
                <TableRow
                  key={po.id}
                  className="align-top"
                  data-state={isSelected(po) ? 'selected' : undefined}
                >
                  {selectable && (
                    <TableCell className="w-10">
                      <SelectBox
                        label={`Select purchase order ${po.po_number}`}
                        checked={isSelected(po)}
                        onChange={(checked) => setSelected(po, checked)}
                      />
                    </TableCell>
                  )}
                  <TableCell>
                    <div className="space-y-0.5">
                      <div className="flex items-center gap-2">
                        <Link
                          to="/purchase-orders/$poId"
                          params={{ poId: po.uuid }}
                          className="font-medium text-primary hover:underline"
                        >
                          {po.po_number}
                        </Link>
                        <OpenActionsBadge count={po.open_action_count ?? 0} />
                      </div>
                      {po.manufacturer && (
                        <p className="text-xs">
                          <Link
                            to="/manufacturers/$manuId"
                            params={{ manuId: po.manufacturer.uuid }}
                            className="text-muted-foreground hover:text-primary hover:underline"
                          >
                            {po.manufacturer.short_name}
                          </Link>
                        </p>
                      )}
                      <div className="flex flex-wrap items-center gap-1.5">
                        <PoStatuses po={po} />
                        <span className="text-xs text-muted-foreground">
                          {parseUTCDate(po.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="max-w-[220px] whitespace-normal">
                    <div className="space-y-0.5">
                      {order?.order_number ? (
                        project ? (
                          <Link
                            to="/projects/$projectId"
                            params={{ projectId: project.uuid }}
                            className="font-medium text-primary hover:underline"
                          >
                            #{order.order_number}
                          </Link>
                        ) : (
                          <p className="font-medium">#{order.order_number}</p>
                        )
                      ) : null}
                      {projectName && project && (
                        <p className="text-xs">
                          <Link
                            to="/projects/$projectId"
                            params={{ projectId: project.uuid }}
                            className="hover:text-primary hover:underline"
                          >
                            {projectName}
                          </Link>
                        </p>
                      )}
                      {(customerName || location) && (
                        <p className="text-xs text-muted-foreground">
                          {customerName &&
                            (order?.customer_group ? (
                              <Link
                                to="/customers/$groupId"
                                params={{ groupId: order.customer_group.uuid }}
                                className="hover:text-primary hover:underline"
                              >
                                {customerName}
                              </Link>
                            ) : (
                              customerName
                            ))}
                          {customerName && location ? ' · ' : ''}
                          {location}
                        </p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="hidden min-w-0 max-w-[320px] whitespace-normal xl:table-cell">
                    <ProductSummaryList
                      items={productItems(po)}
                      moreHref={{ to: '/purchase-orders/$poId', params: { poId: po.uuid } }}
                    />
                  </TableCell>
                  <TableCell className="max-w-[340px] whitespace-normal">
                    <ShippingSummary po={po} linkReference />
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    <p className="text-sm font-semibold">{formatMoney(po.total)}</p>
                    {quotedFreight != null && (
                      <p className="text-[11px] text-muted-foreground">
                        Freight {formatMoney(quotedFreight)}
                      </p>
                    )}
                  </TableCell>
                  <TableCell className="w-10">
                    {po.pdf_filename && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        aria-label={`Download ${po.po_number} PDF`}
                        onClick={() => onDownloadPdf(po.uuid, po.pdf_filename ?? undefined)}
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>

      {/* Mobile cards */}
      <div className="space-y-2 lg:hidden">
        {items.map((po) => {
          const order = po.internal_order
          const customerName = order?.customer_group?.name ?? po.ship_to_name
          const products = productItems(po)
          const download = (e: MouseEvent) => {
            e.preventDefault()
            e.stopPropagation()
            onDownloadPdf(po.uuid, po.pdf_filename ?? undefined)
          }

          const card = (
            <Link
              key={po.id}
              to="/purchase-orders/$poId"
              params={{ poId: po.uuid }}
              className={
                selectable
                  ? 'block min-w-0 flex-1 p-3 transition-colors hover:bg-muted/40'
                  : 'block rounded-lg border bg-card p-3 transition-colors hover:bg-muted/40'
              }
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="truncate font-medium text-primary">{po.po_number}</p>
                    <OpenActionsBadge count={po.open_action_count ?? 0} />
                  </div>
                  <p className="truncate text-xs text-muted-foreground">
                    {[
                      po.manufacturer?.short_name,
                      order?.order_number ? `#${order.order_number}` : null,
                      customerName,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                </div>
                <div className="shrink-0 text-right text-sm tabular-nums">
                  <p className="font-semibold">{formatMoney(po.total)}</p>
                  <p className="text-[11px] text-muted-foreground">
                    {parseUTCDate(po.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>

              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                <PoStatuses po={po} className="text-[11px]" />
                {po.pdf_filename && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="ml-auto h-7 w-7"
                    aria-label={`Download ${po.po_number} PDF`}
                    onClick={download}
                  >
                    <Download className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>

              <div className="mt-2 empty:hidden">
                <ShippingSummary po={po} linkReference={false} />
              </div>

              {products.length > 0 && (
                <div className="mt-2 space-y-0.5 border-t pt-2">
                  <ProductSummaryList items={products} linkEntities={false} />
                </div>
              )}
            </Link>
          )
          if (!selectable) return card
          return (
            <div
              key={po.id}
              className={cn(
                'flex overflow-hidden rounded-lg border bg-card',
                isSelected(po) && 'border-primary/50 bg-primary/5',
              )}
            >
              <label className="flex w-11 shrink-0 cursor-pointer items-start justify-center border-r pt-3.5">
                <SelectBox
                  label={`Select purchase order ${po.po_number}`}
                  checked={isSelected(po)}
                  onChange={(checked) => setSelected(po, checked)}
                />
              </label>
              {card}
            </div>
          )
        })}
      </div>
    </>
  )
}
